import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Check, Truck, ShieldCheck, RotateCcw, Loader2, Snowflake, Box, ThermometerSnowflake,
  Volume2, Beaker,
} from "lucide-react";
import { useInView } from "framer-motion";
import SEO from "@/components/SEO";
import QtyStepper from "@/components/QtyStepper";
import { useCart } from "@/context/CartContext";
import { useProduct } from "@/context/ProductContext";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { formatGBP } from "@/lib/formatGBP";
import { PRODUCT_IMAGE_ALTS } from "@/lib/site";
import { FAQS } from "@/data/faqs";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

// Product data is supplied by the storefront-wide <ProductProvider> in App.js,
// so this page reads the single shared fetch via useProduct() — no per-page
// network call. (Single-SKU site; PRODUCT_SLUG is the slug seeded in Mongo.)

const BOX_CONTENTS = [
  "PeptiChill cooler unit",
  "UK 3-pin power adapter",
  "Quick-start guide",
  "Peptigen Labs care card",
];

const TRUST_BADGES = [
  { icon: Truck, label: "UK Tracked Delivery" },
  { icon: Box, label: "Compact Design" },
  { icon: ThermometerSnowflake, label: "2–8 °C" },
  { icon: ShieldCheck, label: "12-mo Warranty" },
];

const CONFIDENCE = ["Pre-order open", "Ships July 2026", "14-day returns from delivery"];

const WHY_PEPTICHILL = [
  {
    icon: ThermometerSnowflake,
    title: "Dedicated cold storage",
    body: "A single-purpose cooler for research-use peptides and temperature-sensitive laboratory materials. No food. No clutter. No cross-contamination.",
  },
  {
    icon: Beaker,
    title: "Peptigen Labs sister brand",
    body: "Built to the same UK research-grade standards. The Peptigen Labs identity is printed on the front of every unit.",
  },
  {
    icon: Volume2,
    title: "Quiet & compact",
    body: "195 × 255 × 285 mm and under 28 dB — discreet on any countertop, lab bench or desk.",
  },
];

// Order matters — labels render in this sequence whenever the key is present.
const SPEC_ROWS = [
  ["temperature_range", "Temperature range"],
  ["capacity", "Capacity"],
  ["power", "Power"],
  ["display", "Display"],
  ["lid", "Lid"],
  ["dimensions", "Dimensions"],
  ["weight", "Weight"],
  ["noise_level", "Noise level"],
  ["warranty", "Warranty"],
  ["delivery", "Delivery"],
];

export default function Product() {
  const { product, loading, error } = useProduct();
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("specs");
  const [addingState, setAddingState] = useState("idle");

  const { addItem, openDrawer } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (!product) return undefined;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    const priceValidUntil = `${new Date().getFullYear()}-12-31`;
    script.text = JSON.stringify({
      "@context": "https://schema.org/",
      "@type": "Product",
      name: product.name,
      description: product.short_description,
      image: product.images,
      brand: { "@type": "Brand", name: "PeptiChill" },
      offers: {
        "@type": "Offer",
        url: "https://peptidecooler.co.uk/product",
        priceCurrency: product.currency || "GBP",
        price: (product.price_pennies / 100).toFixed(2),
        availability:
          product.stock_status === "in_stock"
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
        priceValidUntil,
      },
    });
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, [product]);

  const handleAdd = async () => {
    if (!product) return;
    setAddingState("adding");
    try {
      await addItem(product.slug, qty);
      setAddingState("added");
      openDrawer();
      setTimeout(() => setAddingState("idle"), 1200);
    } catch {
      setAddingState("idle");
    }
  };

  const handleBuyNow = async () => {
    if (!product) return;
    setAddingState("adding");
    try {
      await addItem(product.slug, qty);
      navigate("/checkout");
    } catch {
      setAddingState("idle");
    }
  };

  if (loading) {
    return (
      <section className="bg-ice-light py-24" data-testid="page-product-loading">
        <div className="container-pc flex items-center gap-3 text-muted">
          <Loader2 size={18} className="animate-spin" />
          Loading product…
        </div>
      </section>
    );
  }

  if (error || !product) {
    return (
      <section className="bg-ice-light py-24">
        <div className="container-pc text-muted">
          {error || "Product unavailable."}
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO
        title={`${product.name} — ${formatGBP(product.price_pennies)} | PeptiChill`}
        description={product.short_description}
        path="/product"
      />
      <section className="bg-ice-light py-12 lg:py-20" data-testid="page-product">
        <div className="container-pc">
          <Breadcrumb name={product.name} />

          <div className="mt-6 grid lg:grid-cols-[1.1fr,0.9fr] gap-10 lg:gap-14 items-start">
            <Gallery
              images={product.images}
              productName={product.name}
            />

            <div data-testid="product-info-column">
              <p className="eyebrow">PeptiChill</p>
              <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-navy tracking-tightest leading-[1.05]">
                {product.name}
              </h1>

              {/* Live confidence row */}
              <ul
                className="mt-5 flex flex-wrap gap-x-5 gap-y-1 text-sm text-navy/85"
                data-testid="product-confidence-row"
              >
                {CONFIDENCE.map((c) => (
                  <li key={c} className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan inline-block" aria-hidden="true" />
                    {c}
                  </li>
                ))}
              </ul>

              <div className="mt-5 flex items-end gap-4 flex-wrap">
                <div>
                  <div
                    className="text-[11px] uppercase tracking-[0.22em] font-semibold text-navy/80 flex items-center gap-2"
                    data-testid="product-preorder-eyebrow"
                  >
                    <span className="w-1 h-1 rounded-full bg-cyan inline-block" aria-hidden="true" />
                    Pre-order price
                    <span className="w-1 h-1 rounded-full bg-cyan inline-block" aria-hidden="true" />
                  </div>
                  <p
                    className="mt-1 text-4xl lg:text-5xl font-bold text-navy tabular-nums"
                    data-testid="product-price"
                  >
                    {formatGBP(product.price_pennies)}
                  </p>
                </div>
                <p className="text-sm text-muted leading-tight">
                  Ships July 2026<br />
                  UK tracked delivery
                </p>
              </div>

              <ul className="mt-7 flex flex-wrap gap-2" data-testid="product-trust-pills">
                {TRUST_BADGES.map(({ icon: Icon, label }) => (
                  <li key={label} className="pill">
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-navy text-white">
                      <Icon size={11} strokeWidth={2.4} />
                    </span>
                    {label}
                  </li>
                ))}
              </ul>

              <p className="mt-7 text-base text-ink/80 leading-relaxed">
                {product.short_description}
              </p>

              <ul className="mt-6 space-y-2.5" data-testid="product-bullets">
                {product.bullet_points.map((b) => (
                  <li key={b} className="flex gap-3 text-[15px] text-ink">
                    <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-navy text-cyan shrink-0">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex items-center gap-4">
                <span className="text-xs uppercase tracking-widest text-muted">Quantity</span>
                <QtyStepper value={qty} onChange={setQty} testIdPrefix="product-qty" />
              </div>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={handleAdd}
                  disabled={addingState === "adding"}
                  data-testid="product-add-to-cart"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white px-7 py-3.5 text-sm font-semibold tracking-wide hover:brightness-110 transition shadow-card disabled:opacity-70"
                >
                  {addingState === "adding" && <Loader2 size={16} className="animate-spin" />}
                  {addingState === "added" ? "Added to basket" : "Add to Cart"}
                </button>
                <button
                  type="button"
                  onClick={handleBuyNow}
                  disabled={addingState === "adding"}
                  data-testid="product-buy-now"
                  className="inline-flex items-center justify-center rounded-full border border-navy text-navy px-7 py-3.5 text-sm font-semibold tracking-wide hover:bg-ice transition disabled:opacity-70"
                >
                  Pre-order Now
                </button>
              </div>

              <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted">
                <li className="inline-flex items-center gap-1.5">
                  <ShieldCheck size={14} className="text-cyan" strokeWidth={2.2} />
                  Secure checkout
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <RotateCcw size={14} className="text-cyan" strokeWidth={2.2} />
                  14-day returns
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <Truck size={14} className="text-cyan" strokeWidth={2.2} />
                  UK tracked
                </li>
              </ul>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-16 lg:mt-24 max-w-4xl">
            <div
              role="tablist"
              aria-label="Product details"
              className="flex border-b border-silver/60"
              data-testid="product-tabs"
            >
              {[
                ["specs", "Specifications"],
                ["box", "What's in the box"],
                ["faq", "FAQ"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  role="tab"
                  aria-selected={tab === key}
                  data-testid={`product-tab-${key}`}
                  onClick={() => setTab(key)}
                  className={`px-5 py-3 text-sm font-semibold tracking-wide -mb-px border-b-2 transition-colors ${
                    tab === key
                      ? "text-navy border-navy"
                      : "text-muted border-transparent hover:text-navy"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="pt-8" role="tabpanel" data-testid={`product-tabpanel-${tab}`}>
              {tab === "specs" && <SpecsTable specs={product.specs} />}
              {tab === "box" && <BoxContents items={BOX_CONTENTS} />}
              {tab === "faq" && <FaqsTab />}
            </div>
          </div>
        </div>

        {/* Why PeptiChill strip */}
        <div className="mt-16 lg:mt-24 bg-ice py-14 lg:py-16" data-testid="why-peptichill">
          <div className="container-pc">
            <div className="max-w-2xl">
              <p className="eyebrow">Why PeptiChill</p>
              <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-navy tracking-tighter">
                Three reasons it sits on more lab worktops than your kitchen fridge.
              </h2>
            </div>
            <div className="mt-9 grid grid-cols-1 sm:grid-cols-3 gap-5 lg:gap-6">
              {WHY_PEPTICHILL.map(({ icon: Icon, title, body }) => (
                <article
                  key={title}
                  className="rounded-2xl bg-white border border-silver/40 p-6 shadow-card lift"
                  data-testid={`why-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-navy text-cyan">
                    <Icon size={20} strokeWidth={1.9} />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-navy tracking-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-sm text-muted leading-relaxed">{body}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Breadcrumb({ name }) {
  return (
    <nav aria-label="Breadcrumb" className="text-xs text-muted">
      <a href="/" className="hover:text-navy transition-colors">Home</a>
      <span className="mx-2 text-silver">/</span>
      <span className="text-navy/80">{name}</span>
    </nav>
  );
}

// ---------- Gallery (Ken Burns auto-playing carousel) ----------
const HOLD_MS = 4000;
const FADE_MS = 1000;
const STEP_MS = HOLD_MS + FADE_MS;
const RESUME_AFTER_USER_MS = 5000;
const SWIPE_THRESHOLD_PX = 50;

function Gallery({ images, productName }) {
  const reduce = usePrefersReducedMotion();
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0); // 0..1 within the current step
  const lastUserAtRef = useRef(0);
  const rootRef = useRef(null);
  const inView = useInView(rootRef, { amount: 0.3 });

  // Preload all images for snappy transitions — deferred until AFTER the
  // initial paint + window-load to keep our Largest Contentful Paint lean on
  // mobile. The active image is loaded eagerly by its <img> tag anyway, so
  // there's no race.
  useEffect(() => {
    const preload = () => {
      images.forEach((src) => { const img = new Image(); img.decoding = "async"; img.src = src; });
    };
    if (document.readyState === "complete") {
      // Already loaded — defer one frame so we don't block the next paint.
      const id = requestAnimationFrame(preload);
      return () => cancelAnimationFrame(id);
    }
    window.addEventListener("load", preload, { once: true });
    return () => window.removeEventListener("load", preload);
  }, [images]);

  // Auto-play timeline driven with rAF so it pauses cleanly out-of-view and
  // resumes a smooth 5s after the user manually picks a thumb.
  useEffect(() => {
    if (reduce || !inView) return undefined;
    let raf;
    let stepStart = performance.now();
    let stepActive = active;
    const tick = (now) => {
      const sinceUser = now - lastUserAtRef.current;
      if (sinceUser < RESUME_AFTER_USER_MS) {
        // While the user has interacted recently, hold on the active image
        // (no Ken Burns drift either — they want a clean look at it).
        stepStart = now;
        stepActive = active;
        setProgress(0);
        raf = requestAnimationFrame(tick);
        return;
      }
      const elapsed = now - stepStart;
      if (elapsed >= STEP_MS) {
        stepActive = (stepActive + 1) % images.length;
        setActive(stepActive);
        stepStart = now;
        setProgress(0);
      } else {
        setProgress(elapsed / STEP_MS);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // `active` is only used to seed stepActive on mount/restart; subsequent
    // changes are tracked inside the rAF loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduce, inView, images.length]);

  const markUser = () => { lastUserAtRef.current = performance.now(); };
  const goTo = (i) => {
    setActive(((i % images.length) + images.length) % images.length);
    setProgress(0);
    markUser();
  };
  const step = (delta) => goTo(active + delta);

  // Keyboard navigation when the gallery has focus
  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
  };

  // Touch swipe
  const touchStartXRef = useRef(0);
  const onTouchStart = (e) => { touchStartXRef.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const dx = (e.changedTouches?.[0]?.clientX ?? 0) - touchStartXRef.current;
    if (Math.abs(dx) >= SWIPE_THRESHOLD_PX) step(dx < 0 ? 1 : -1);
  };

  // Ken Burns transform for the active image — runs across the HOLD phase only.
  // hold:  scale 1 → 1.04, translate (0,0) → (-8,-4) over HOLD_MS
  // fade:  active holds its final transform while fading out; next starts at
  //        scale 1.05 settling to 1.00 (handled inline below).
  const holdT = Math.min(1, (progress * STEP_MS) / HOLD_MS); // 0..1 during hold, 1 during fade
  const kbScale = reduce ? 1 : 1 + 0.04 * holdT;
  const kbTx = reduce ? 0 : -8 * holdT;
  const kbTy = reduce ? 0 : -4 * holdT;

  // Crossfade — once we enter the FADE window of the active step, the NEXT
  // image opacity goes 0→1 over the remaining duration.
  const fadeT = progress * STEP_MS <= HOLD_MS
    ? 0
    : Math.min(1, ((progress * STEP_MS) - HOLD_MS) / FADE_MS);
  const nextIdx = (active + 1) % images.length;
  const showingNext = !reduce && fadeT > 0;

  const altText = (i) => PRODUCT_IMAGE_ALTS[i] || `${productName} — view ${i + 1}`;
  // Studio shots (front 3/4, alt angle, lid open) get the multiply blend so
  // their white background dissolves into the section. The lifestyle / marble
  // shot at index 3 is a real scene — leave it untouched.
  const isStudio = (i) => i !== 3;

  return (
    <div
      ref={rootRef}
      data-testid="product-gallery"
      role="region"
      aria-roledescription="image carousel"
      aria-label={`${productName} image gallery, image ${active + 1} of ${images.length}`}
      aria-live="polite"
      tabIndex={0}
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-4 focus-visible:ring-offset-white rounded-2xl"
    >
      <figure
        className="relative rounded-2xl bg-white border border-silver/40 overflow-hidden shadow-card aspect-square"
        data-testid="product-gallery-stage"
      >
        {/* Active image */}
        <img
          key={`a-${active}`}
          src={images[active]}
          alt={altText(active)}
          data-testid="product-gallery-main"
          className={`absolute inset-0 w-full h-full object-contain will-change-transform${isStudio(active) ? " blend-product" : ""}`}
          style={{
            transform: `translate(${kbTx}px, ${kbTy}px) scale(${kbScale})`,
            transition: "transform 60ms linear",
            opacity: showingNext ? 1 - fadeT : 1,
          }}
          loading="eager"
          decoding="async"
        />
        {/* Incoming next image — fades in + settles from scale 1.05 → 1.00 */}
        {showingNext && (
          <img
            key={`b-${nextIdx}`}
            src={images[nextIdx]}
            alt=""
            aria-hidden="true"
            className={`absolute inset-0 w-full h-full object-contain${isStudio(nextIdx) ? " blend-product" : ""}`}
            style={{
              opacity: fadeT,
              transform: `scale(${1.05 - 0.05 * fadeT})`,
              transition: "none",
            }}
          />
        )}
      </figure>

      {/* Thumbnails */}
      <div
        className="mt-4 grid gap-2 sm:gap-3"
        style={{ gridTemplateColumns: `repeat(${images.length}, minmax(0, 1fr))` }}
        data-testid="product-gallery-thumbs"
      >
        {images.map((src, i) => {
          const isActive = i === active;
          const thumbProgress = isActive && !reduce ? Math.min(1, progress) : 0;
          return (
            <button
              key={src}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`View ${i + 1} of ${images.length}: ${altText(i)}`}
              aria-current={isActive ? "true" : undefined}
              data-testid={`product-gallery-thumb-${i}`}
              className={`relative rounded-xl overflow-hidden border-2 transition aspect-square bg-white ${
                isActive ? "border-cyan shadow-card" : "border-silver/40 hover:border-navy/40 hover:-translate-y-0.5"
              }`}
            >
              <img
                src={src}
                alt=""
                className={`block w-full h-full object-cover${isStudio(i) ? " blend-product" : ""}`}
                loading="lazy"
              />
              {isActive && (
                <span
                  aria-hidden="true"
                  data-testid={`product-gallery-thumb-progress-${i}`}
                  className="absolute left-0 bottom-0 h-1 bg-cyan transition-[width] duration-100 ease-linear"
                  style={{ width: `${thumbProgress * 100}%` }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SpecsTable({ specs }) {
  const rows = SPEC_ROWS
    .map(([key, label]) => [label, specs?.[key]])
    .filter(([, value]) => Boolean(value));
  return (
    <dl
      className="divide-y divide-silver/60 border-y border-silver/60"
      data-testid="product-specs-table"
    >
      {rows.map(([label, value]) => (
        <div key={label} className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 py-4">
          <dt className="sm:col-span-5 text-sm text-muted">{label}</dt>
          <dd className="sm:col-span-7 text-[15px] font-semibold text-navy">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function BoxContents({ items }) {
  return (
    <ul className="space-y-3" data-testid="product-box-contents">
      {items.map((item) => (
        <li key={item} className="flex gap-3 text-[15px] text-ink">
          <span className="mt-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-navy text-cyan shrink-0">
            <Snowflake size={11} strokeWidth={2.6} />
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function FaqsTab() {
  return (
    <Accordion
      type="single"
      collapsible
      className="rounded-2xl border border-silver/60 bg-white divide-y divide-silver/60 overflow-hidden"
      data-testid="product-faq-accordion"
    >
      {FAQS.map((item, i) => (
        <AccordionItem key={item.q} value={`pdp-${i}`} className="border-b-0">
          <AccordionTrigger className="px-5 py-4 text-left text-[15px] font-semibold text-navy hover:no-underline hover:bg-ice/60 [&[data-state=open]]:bg-ice/40 transition-colors">
            {item.q}
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-5 pt-0 text-sm text-ink/75 leading-[1.75]">
            {item.a}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
